import React from "react";
import type { RedditPost } from "../../../../context/RedditFeedContext";
import { RedditFeedList, formatGeneratedAt } from "../../../Reddit/RedditFeedList";

interface RedditFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Feed state comes from useRedditFeed, which lives in the parent so the
   * request survives this modal being closed. This component only renders.
   */
  posts: RedditPost[];
  queriesUsed: string[];
  isLoading: boolean;
  /** A background refresh is running while the board stays on screen — unlike
   *  isLoading, this never blanks the panel. */
  isRefreshing?: boolean;
  error: string | null;
  generatedAt: string | null;
  onRefresh: () => void;
  regeneratingUrl: string | null;
  onRegenerate: (url: string, words: number) => void;
  answeredUrls: Set<string>;
  onToggleAnswered: (url: string) => void;
  /** True when the user has replied to several threads in a short window. */
  pacingWarning?: boolean;
}

/**
 * Dialog chrome for the Reddit feed: header, backdrop, footer. The card list
 * itself — loading/empty/error states, each post's card — lives in the
 * shared RedditFeedList, also used by the Content > Reddit tab.
 */
export const RedditFeedModal: React.FC<RedditFeedModalProps> = ({
  isOpen,
  onClose,
  posts,
  queriesUsed,
  isLoading,
  isRefreshing = false,
  error,
  generatedAt,
  onRefresh,
  regeneratingUrl,
  onRegenerate,
  answeredUrls,
  onToggleAnswered,
  pacingWarning = false,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 flex w-full max-w-2xl flex-col rounded-t-2xl sm:rounded-2xl border border-theme-border bg-theme-surface shadow-2xl max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-theme-border px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-100">
              <svg className="h-4 w-4 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 0C4.478 0 0 4.478 0 10s4.478 10 10 10 10-4.478 10-10S15.522 0 10 0zm5.896 10.52a1.3 1.3 0 01-.384.922 3.33 3.33 0 01.064.62c0 3.152-3.67 5.71-8.194 5.71-4.524 0-8.194-2.558-8.194-5.71 0-.213.023-.422.066-.624a1.302 1.302 0 11.318-2.437 6.367 6.367 0 013.448-1.728l.584-2.746a.34.34 0 01.405-.258l1.938.406a.912.912 0 111.8.352l-1.76-.37-.52 2.44c1.302.063 2.492.424 3.448 1.006a1.302 1.302 0 11.286 2.437z" />
              </svg>
            </span>
            <div>
              <p className="text-sm font-semibold text-theme-fg">Reddit Feed</p>
              {queriesUsed.length > 0 && !isLoading && (
                <p className="text-[11px] text-theme-fg-muted">
                  Searching: {queriesUsed.slice(0, 2).join(" · ")}
                  {formatGeneratedAt(generatedAt) && (
                    <span> · updated {formatGeneratedAt(generatedAt)}</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading || isRefreshing}
              className="flex h-7 w-7 items-center justify-center rounded-full text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 disabled:opacity-40"
              title="Find new posts"
            >
              <svg className={`h-4 w-4 ${(isLoading || isRefreshing) ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-full text-theme-fg-muted transition-colors hover:bg-theme-secondary/10"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          <RedditFeedList
            posts={posts}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            error={error}
            generatedAt={generatedAt}
            onRefresh={onRefresh}
            regeneratingUrl={regeneratingUrl}
            onRegenerate={onRegenerate}
            answeredUrls={answeredUrls}
            onToggleAnswered={onToggleAnswered}
            pacingWarning={pacingWarning}
            onDismissLoading={onClose}
          />
        </div>

        <div className="border-t border-theme-border px-5 py-3">
          <p className="text-[11px] text-theme-fg-muted text-center">
            Ranked by relevance · Open a thread to reply
          </p>
        </div>
      </div>
    </div>
  );
};
